import { Dialog } from '@headlessui/react';

import type { ItemAvailability } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import {
  type ExpenseLineItem,
  type LineItemsRest,
  formatAmountForCurrency,
} from '@splitfiesta/shared';
import { Alert, Button } from '@splitfiesta/ui';

import { LineItemRow } from './LineItemRow';
import { RestRow } from './RestRow';

export interface SplitByItemClaimProps {
  /** The rows as the viewer would save them; `takenBy` in the order the names should read. */
  rows: Array<{
    item: ExpenseLineItem;
    availability: ItemAvailability;
    unpickedHint?: boolean;
  }>;
  nameOf: (userId: string) => string;
  /** The entry currency, the one printed on the receipt. */
  currency: string;
  rest: {
    kind: 'rest' | 'restNegative';
    mode: LineItemsRest['mode'];
    amount: number;
  } | null;
  /** The viewer's items and part of the tip, in `currency`; null while there is no preview. */
  footer: { itemsAmount: number; tip: number } | null;
  /** What the viewer pays, as the button reads it. */
  total: { amount: number; currency: string };
  /** The total in the group currency, only when the expense is foreign. */
  groupShare?: { amount: number; currency: string };
  dirty: boolean;
  /** Changed picks, or nothing ticked yet on a bill the table was asked to pick. */
  canSave: boolean;
  saving: boolean;
  /** Offline or saving: the circles, steppers and Save do not respond. */
  disabled: boolean;
  error: string | null;
  onToggle: (itemId: string) => void;
  onUnits: (itemId: string, units: number) => void;
  onSave: () => void;
}

interface SplitByItemClaimContentProps extends SplitByItemClaimProps {
  /** The dialog's close request, so unsaved picks are confirmed first. */
  onCancel: () => void;
}

/** The detail's claim: the viewer picks their own units on a fixed list and saves them in one go. */
export function SplitByItemClaimContent({
  rows,
  nameOf,
  currency,
  rest,
  footer,
  total,
  groupShare,
  canSave,
  saving,
  disabled,
  error,
  onToggle,
  onUnits,
  onSave,
  onCancel,
}: SplitByItemClaimContentProps) {
  const { t } = useTranslation('expenses');
  const totalLabel = formatAmountForCurrency(total.amount, total.currency);

  return (
    <>
      <Dialog.Title
        as="h2"
        className="text-xl font-semibold text-gray-900 dark:text-gray-100"
      >
        {t('lineItems.pickWhatYouHad')}
      </Dialog.Title>

      {error ? <Alert type="error" message={error} className="mt-3" /> : null}

      <ul className="mt-3">
        {rows.map(({ item, availability, unpickedHint }) => (
          <LineItemRow
            key={item.id}
            item={item}
            availability={availability}
            nameOf={nameOf}
            currency={currency}
            unpickedHint={unpickedHint}
            disabled={disabled}
            onToggle={() => onToggle(item.id)}
            onUnits={units => onUnits(item.id, units)}
          />
        ))}
      </ul>

      {rest ? (
        <RestRow
          testId="claim-rest"
          label={t(
            rest.kind === 'restNegative'
              ? 'lineItems.restNegative'
              : 'lineItems.rest'
          )}
          amount={formatAmountForCurrency(rest.amount, currency)}
          mode={rest.mode}
        />
      ) : null}

      <div
        data-testid="claim-footer"
        className="sticky bottom-0 -mx-4 -mb-5 mt-4 border-t border-gray-200 bg-white px-4 pb-5 pt-3 sm:-mx-6 sm:px-6 dark:border-gray-700 dark:bg-gray-800"
      >
        <div className="flex flex-wrap items-center gap-3">
          <div
            data-testid="claim-your-share"
            className="flex min-w-[45%] flex-1 flex-wrap items-baseline gap-x-2"
          >
            {footer ? (
              <p
                data-testid="claim-footer-line"
                className="w-full text-sm text-gray-600 dark:text-gray-400"
              >
                {t('picks.claim.footer', {
                  amount: formatAmountForCurrency(footer.itemsAmount, currency),
                  tip: formatAmountForCurrency(footer.tip, currency),
                })}
              </p>
            ) : null}
            <span
              data-testid="claim-total"
              className="text-xl font-bold tabular-nums text-gray-900 dark:text-gray-100"
            >
              {totalLabel}
            </span>
            {groupShare ? (
              <span
                data-testid="claim-group-share"
                className="text-sm tabular-nums text-gray-500 dark:text-gray-400"
              >
                {formatAmountForCurrency(
                  groupShare.amount,
                  groupShare.currency
                )}
              </span>
            ) : null}
          </div>
          <div className="ml-auto flex flex-wrap gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={saving}
              className="min-h-11"
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={onSave}
              disabled={!canSave || disabled}
              loading={saving}
              className="min-h-11"
            >
              {t('picks.claim.save', { total: totalLabel })}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export default SplitByItemClaimContent;
